import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <p className="text-muted-foreground">Invalid or missing reset link.</p>
            <Button variant="link" onClick={() => navigate('/login')} className="mt-2 text-orange-500">Back to login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <Zap className="w-10 h-10 text-orange-500" />
          <span className="text-3xl font-extrabold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>EmergencyPOS</span>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="text-center pb-2">
            {success ? (
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            ) : (
              <Lock className="w-10 h-10 text-orange-500 mx-auto mb-2" />
            )}
            <h2 className="text-lg font-bold">{success ? 'Password Reset!' : 'Set New Password'}</h2>
          </CardHeader>
          <CardContent className="pt-2">
            {success ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">Your password has been changed. You can now log in.</p>
                <Button onClick={() => navigate('/login')} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="go-to-login-btn">Go to Login</Button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-12 text-base"
                    placeholder="Min 6 characters"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    className="h-12 text-base"
                    placeholder="Repeat password"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="reset-password-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
